import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, MapPin } from "lucide-react";

export default function AddressAutocomplete({ value, onChange, onSelect, testId = "addr-search" }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const wrap = useRef(null);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 4) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/geo/search", { params: { q } });
        setResults(data);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (r) => {
    onSelect(r);
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrap}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          data-testid={testId}
          className="pl-9"
          placeholder="Start typing an address to search…"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => { if (results.length) setOpen(true); }}
        />
        {loading && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-mono-plex">…</div>}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-md shadow-md max-h-64 overflow-y-auto" data-testid="addr-results">
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() => pick(r)}
              data-testid={`addr-result-${i}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2 border-b border-slate-100 last:border-b-0 transition-colors duration-200"
            >
              <MapPin className="w-3.5 h-3.5 text-slate-400 mt-1 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm text-slate-900 truncate">{r.street || r.label.split(",")[0]}</div>
                <div className="text-xs text-slate-500 truncate font-mono-plex">
                  {[r.city, r.county && `${r.county} County`, r.state, r.zip_code].filter(Boolean).join(" · ")}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
